import {CartCompanyResponse} from 'api/dataTypes';
import {validateEmail, validatePhoneNumber} from 'lib/validate';
import {CartFormDeliveryInfo} from 'modules/Cart/components/CartFormDeliveryInfo/CartFormDeliveryInfo';
import {PickupPoint} from 'modules/Cart/components/PickupPoint/PickupPoint';
import {CustomLocation} from 'modules/Cart/containers/CartForm/CustomLocation/CustomLocation';
import {SelectItemCompany} from 'modules/Cart/containers/CartForm/SelectItemCompany/SelectItemCompany';
import {
    CartDeliveryType,
    useCartForm,
} from 'modules/Cart/containers/CartForm/useCartForm';
import {CartFormPaymentOptions} from 'modules/Cart/containers/CartFormPaymentOptions/CartFormPaymentOptions';
import {CartFormPickupPoints} from 'modules/Cart/containers/CartFormPickupPoints/CartFormPickupPoints';
import {CART_ORDER_FORM_ID} from 'modules/Cart/lib/constants';
import {FC, useMemo} from 'react';

import {CheckboxHookForm} from 'components/CheckboxHookForm/CheckboxHookForm';
import {Select} from 'components/Select/Select';
import {Switch} from 'components/Switch/Switch';
import {Text} from 'components/Text/Text';
import {TextFieldHookForm} from 'components/TextFieldHookForm/TextFieldHookForm';

import styles from './CartForm.module.css';

export const CART_DELIVERY_TABS = [
    {label: 'Доставка', id: CartDeliveryType.Delivery},
    {label: 'Самовывоз', id: CartDeliveryType.Pickup},
];

type Props = {};
export const CartForm: FC<Props> = () => {
    const {
        control,
        deliveryType,
        pickupPoint,
        payment,
        handleChangeDeliveryTab,
        handleSubmit,
        handleSelectPickupPoint,
        handleSelectPayment,
        isAuth,
        phoneIsTouched,
        companies,
        selectedCompanyId,
        handleSelectCompany,
        pickupPoints,
        savedLocations,
        selectedDeliveryLocation,
        makeHandleSelectLocation,
        makeHandleFocus,
        shouldShowErrorTooltipFields,
    } = useCartForm();

    const renderItemMenu = (item: CartCompanyResponse) => {
        return <SelectItemCompany title={item.title} inn={item.inn} />;
    };

    const deliveryTabs = useMemo(() => {
        if (!pickupPoints || pickupPoints.length) {
            return CART_DELIVERY_TABS;
        }

        return CART_DELIVERY_TABS.filter(
            (tab) => tab.id !== CartDeliveryType.Pickup,
        );
    }, [pickupPoints]);

    return (
        <form
            id={CART_ORDER_FORM_ID}
            className={styles.block}
            onSubmit={handleSubmit}
        >
            <div className={styles.section}>
                <Text.Responsive
                    breakpointsMap={{default: 'Bold20', sm: 'Bold24'}}
                    className={styles.sectionTitle}
                >
                    Контактные данные
                </Text.Responsive>

                <div className={styles.formGroup}>
                    <TextFieldHookForm
                        label="Имя"
                        name="name"
                        control={control}
                        className={styles.input}
                        required
                        onFocus={makeHandleFocus('name')}
                        hideErrorTooltip={!shouldShowErrorTooltipFields.name}
                    />
                    <TextFieldHookForm
                        label="Фамилия"
                        name="surname"
                        control={control}
                        className={styles.input}
                        required
                        onFocus={makeHandleFocus('surname')}
                        hideErrorTooltip={!shouldShowErrorTooltipFields.surname}
                    />
                    {!isAuth && (
                        <TextFieldHookForm
                            label="Название компании"
                            name="companyName"
                            control={control}
                            className={styles.input}
                            required
                            onFocus={makeHandleFocus('companyName')}
                            hideErrorTooltip={
                                !shouldShowErrorTooltipFields.companyName
                            }
                        />
                    )}
                    {isAuth && (
                        <Select
                            name="companyId"
                            label="Компания, оплачивающая заказ"
                            className={styles.input}
                            renderItemMenu={renderItemMenu}
                            options={companies}
                            value={selectedCompanyId}
                            onSelect={handleSelectCompany}
                        />
                    )}
                    <TextFieldHookForm
                        label="Телефон"
                        name="phone"
                        control={control}
                        className={styles.input}
                        validate={
                            phoneIsTouched ? validatePhoneNumber : undefined
                        }
                        mask="+7 999 999 99 99"
                        required
                        onFocus={makeHandleFocus('phone')}
                        hideErrorTooltip={!shouldShowErrorTooltipFields.phone}
                    />
                    <TextFieldHookForm
                        label="E-mail"
                        name="email"
                        control={control}
                        className={styles.input}
                        validate={validateEmail}
                        required
                        onFocus={makeHandleFocus('email')}
                        hideErrorTooltip={!shouldShowErrorTooltipFields.email}
                    />
                </div>
            </div>

            <div className={styles.section}>
                <Text.Responsive
                    breakpointsMap={{default: 'Bold20', sm: 'Bold24'}}
                    className={styles.sectionTitle}
                >
                    Способ доставки
                </Text.Responsive>

                <Switch
                    switches={deliveryTabs}
                    selectedSwitchId={deliveryType}
                    onChange={handleChangeDeliveryTab}
                    className={styles.tabs}
                />

                {deliveryType === CartDeliveryType.Delivery && (
                    <>
                        {isAuth && savedLocations.length > 0 ? (
                            <div className={styles.locations}>
                                {savedLocations.map((location) => (
                                    <PickupPoint
                                        key={location.address}
                                        title={location.address}
                                        selected={
                                            selectedDeliveryLocation?.id ===
                                            location.id
                                        }
                                        onSelect={makeHandleSelectLocation(
                                            location,
                                        )}
                                    />
                                ))}

                                <CustomLocation
                                    title="Другой адрес"
                                    onSelect={makeHandleSelectLocation('new')}
                                    selected={!selectedDeliveryLocation}
                                >
                                    <TextFieldHookForm
                                        label="Город, улица, дом"
                                        name="address"
                                        control={control}
                                    />
                                </CustomLocation>
                            </div>
                        ) : (
                            <TextFieldHookForm
                                label="Город, улица, дом"
                                name="address"
                                control={control}
                            />
                        )}

                        <CartFormDeliveryInfo className={styles.deliveryInfo} />
                    </>
                )}
                {deliveryType === CartDeliveryType.Pickup && (
                    <CartFormPickupPoints
                        onSelect={handleSelectPickupPoint}
                        selectedPoint={pickupPoint}
                        points={pickupPoints || []}
                    />
                )}
            </div>

            <div className={styles.section}>
                <Text.Responsive
                    breakpointsMap={{default: 'Bold20', sm: 'Bold24'}}
                    className={styles.sectionTitle}
                >
                    Способ оплаты
                </Text.Responsive>

                <CartFormPaymentOptions
                    onSelect={handleSelectPayment}
                    selectedOption={payment.id}
                />
            </div>

            <div className={styles.section}>
                <TextFieldHookForm
                    label="Комментарий к заказу"
                    name="comment"
                    control={control}
                    nativeType="textarea"
                />
            </div>

            <div className={styles.section}>
                <CheckboxHookForm
                    name="agreement"
                    control={control}
                    label="Да, я согласен получать новости о самых выгодных предложениях компании «Клен»"
                />
            </div>
        </form>
    );
};
